import { useContext, useEffect, useMemo } from 'react';
import { eventBus } from 'services/eventBus';
import dayjs from 'lib/dayjs';
import { Modal, Select, Tooltip, message, Dropdown, Menu, Space } from 'antd';
import { ReactComponent as NoSvg } from '../assets/no.svg';
import { ReactComponent as AnchorSvg } from '../assets/anchor.svg';
import { ReactComponent as TransferSvg } from '../assets/transfer.svg';
import Icon, { CopyOutlined, DownOutlined } from '@ant-design/icons';
import copyText from 'utils/copyText';
import styled from '@emotion/styled';
import { useAssistants } from './useApi';
import useModal from 'hooks/useModal';
import { useFormikContext } from 'formik';
import { ReadonlyContext } from './context';
import { useTypeLabel, officialTypeId, isOfficialType } from 'views/Todo/hooks/useLabel';
import { TypeSourceContext } from './TypeSourceContext';
import { findIndex, get } from 'lodash-es';
import FormField from 'components/FormField';
import { useMemoizedFn } from 'ahooks';
import { Status, TodoSubType } from './FormItem';
import { useMessageById } from 'hooks/business/message';

function Profile(props: { disabled?: boolean }) {
  const { disabled = false } = props;
  const { values, setValues, handleSubmit } = useFormikContext<Todo.TodoInstance>();
  const { typeSource } = useContext(TypeSourceContext);
  const {
    id,
    todoStatus,
    type,
    typeName,
    relatedMsgId,
    createTime,
    operator,
    operatorName,
    storeId,
    subType,
  } = values;

  const msg = useMessageById(relatedMsgId);

  useEffect(() => {
    if (!msg) {
      eventBus.emit('LOAD_MORE_MESSAGE');
    }
  }, [msg]);

  const readonly = useContext(ReadonlyContext);

  const { color1 } = useMemo(() => {
    return (
      {
        1: {
          color1: 'var(--fett-surface-error-text)',
          color2: '#FFD6D6',
        },
        2: {
          color1: '#3370FF',
          color2: 'var(--fett-chat-bubble-agent)',
        },
        3: {
          color1: 'var(--fett-text-quaternary)',
          color2: 'var(--fett-border-strong)',
        },
        4: {
          color1: '#FFA940',
          color2: '#FFE7BA',
        },
      }[todoStatus] ?? {
        color1: '#a4a4a4',
        color2: '#a4a4a4',
      }
    );
  }, [todoStatus]);

  const { getTypeLabel, t } = useTypeLabel();

  const { data } = useAssistants(storeId);

  const { visible, show, hide, meta } = useModal<string>();

  const retrieveTypeName = useMemoizedFn((type: number) => {
    if (type === -999) {
      return `${t('common.status.deleted')}-${typeName}`;
    }
    if (type === -998) {
      return typeName;
    } else {
      if (officialTypeId.includes(type)) {
        return getTypeLabel(type);
      } else {
        return typeSource?.find((item) => item.type === type)?.typeLabel;
      }
    }
  });

  const typeLabel = useMemo(() => {
    return retrieveTypeName(type) || typeName;
  }, [type, retrieveTypeName, typeName]);

  const subTypeOptions = useMemo(() => {
    const index = findIndex(typeSource, (o) => o.type === type);
    return get(typeSource, `${index}.subTypeTrees`) as unknown as any[];
  }, [typeSource, type]);

  const modal = (
    <Modal
      width={460}
      title={t('scene.chatWorkspace.taskTab.action.transfer')}
      visible={visible}
      onCancel={() => hide()}
      onOk={() => {
        if (meta !== operator) {
          setValues({ ...values, operator: meta });
          handleSubmit();
        }
        hide();
      }}
    >
      <div>{t('domain.task.field.assignedAgent')}</div>
      <Select
        placeholder={t('common.validation.requiredSelection', {
          fieldName: t('domain.task.field.assignedAgent'),
        })}
        value={meta === '0' ? undefined : meta || undefined}
        onChange={show}
        options={data?.map(({ id, name }: any) => ({ value: id, label: name }))}
        style={{ width: '100%', marginTop: '6px' }}
      />
    </Modal>
  );

  return (
    <>
      <Container borderLeftColor={color1}>
        <section>
          <div>
            <Dropdown
              placement="bottomRight"
              trigger={['click']}
              disabled={disabled || readonly}
              overlay={
                <Menu
                  onClick={(e) => {
                    const selected = typeSource?.find((item) => item.type === Number(e.key));
                    if (selected) {
                      setValues({
                        ...values,
                        type: selected.type,
                        typeName: isOfficialType(Number(e.key)) ? '' : selected.typeLabel,
                      });
                      handleSubmit();
                    }
                  }}
                >
                  {typeSource?.map((item) => {
                    if (item.type === type) return null;
                    return (
                      <Menu.Item key={item.type} onClick={() => {}}>
                        {retrieveTypeName(item.type)}
                      </Menu.Item>
                    );
                  })}
                </Menu>
              }
            >
              <Space>
                <h4>{typeLabel}</h4>
                {!(disabled || readonly) && <DownOutlined style={{ fontSize: 11 }} />}
              </Space>
            </Dropdown>
          </div>
          <Status color={color1} />
          <div>
            {msg && (
              <Tooltip
                arrowPointAtCenter
                placement="topLeft"
                title={t('scene.chatWorkspace.taskTab.detail.action.jumpToOrigin')}
              >
                <Icon
                  component={AnchorSvg}
                  onClick={() => eventBus.emit('SCROLL_TO_MSG', relatedMsgId)}
                />
              </Tooltip>
            )}
            <Tooltip
              arrowPointAtCenter
              placement="topLeft"
              title={
                <div className="flex items-center">
                  <div className="mr-[2px]">
                    {t('domain.task.field.taskId')}: {id}
                  </div>
                  <CopyOutlined
                    onClick={() =>
                      copyText(id).then(() => message.success(t('common.feedback.copySuccess')))
                    }
                  />
                </div>
              }
            >
              <Icon component={NoSvg} />
            </Tooltip>
          </div>
        </section>
        <section>
          <Light>
            {t('domain.task.field.assignedAgent')}：{operatorName || '-'}
            {todoStatus !== 3 && !readonly && (
              <Tooltip title={t('scene.chatWorkspace.taskTab.action.transfer')}>
                <Icon
                  component={TransferSvg}
                  style={{ marginTop: '-4px' }}
                  onClick={() => show(operator)}
                />
              </Tooltip>
            )}
            {modal}
          </Light>
          <Light>
            {t('scene.chatWorkspace.taskTab.detail.label.createdAt', {
              x: dayjs(Number(createTime)).format('YYYY-MM-DD HH:mm:ss'),
            })}
          </Light>
        </section>
      </Container>

      {!!subTypeOptions?.length && (!disabled || (disabled && subType)) && (
        <FormField
          name="i-dont-care-fk"
          label={t('scene.chatWorkspace.taskTab.form.label.subType')}
          className="!mt-[16px] !mb-[12px]"
        >
          {() => {
            const v = [];
            if (values.subType) {
              v.push(values.subType);
              if (values.subType2) {
                v.push(values.subType2);
                if (values.subType3) {
                  v.push(values.subType3);
                }
              }
            }
            return (
              <TodoSubType
                disabled={disabled || readonly}
                value={v}
                onChange={(v) => {
                  const [subType = '', subType2 = '', subType3 = ''] = v;
                  setValues({
                    ...values,
                    subType,
                    subType2,
                    subType3,
                  });
                  handleSubmit();
                }}
                dataSource={subTypeOptions}
              />
            );
          }}
        </FormField>
      )}
    </>
  );
}

const Container = styled.div<{ borderLeftColor: string }>`
  padding: 8px 12px 8px 15px;
  border-left: solid 4px ${({ borderLeftColor }) => borderLeftColor};
  border-bottom: solid 1px var(--fett-border-default);
  border-radius: 4px 0 0 0;
  > section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    > div {
      display: flex;
      align-items: center;
      h4 {
        margin-bottom: 0;
        font-weight: bold;
        color: var(--fett-text-primary);
      }
      > .anticon {
        font-size: 16px;
        margin-left: 6px;
        cursor: pointer;
      }
    }
    & + section {
      margin-top: 6px;
    }
    &:first-of-type {
      > div {
        flex: 3 0 0;
      }
      > div:first-of-type {
        flex: 2 0 0;
        margin-right: 4px;
      }
      > div:last-of-type {
        flex: 0 0 auto;
      }
    }
  }
`;

const Light = styled.div`
  font-size: 12px;
  color: var(--fett-text-tertiary);
`;

export default Profile;
